import { useState, useEffect } from 'react';
import BarcodeScanner from '../components/BarcodeScanner';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

// .button ขนาดเต็มใหญ่เกินไปสำหรับปุ่มเลือกทั้งหมด/ไม่เลือกเลยในพรีวิวโคลน และ index.css
// ยังไม่มีคลาสปุ่มไซซ์เล็ก (.button-secondary เป็นธีมมืด ใช้บนพื้นขาวของ Modal ไม่ได้)
const smallButton = { padding: '0.35rem 0.85rem', fontSize: '0.8rem' };

// แอดมิน (Vercel) กับ API (Railway) ขึ้นคนละที่ ไม่ได้ขึ้นพร้อมกัน — มีช่วงที่หน้าเว็บใหม่
// คุยกับ API ตัวเก่าที่ยังไม่รู้จัก itemIds ซึ่งจะเมิน itemIds แล้วยกมาทั้งสาขาเงียบ ๆ ทั้งที่
// ผู้ใช้ติ๊กไว้แค่ไม่กี่รายการ กันด้วยการเช็คว่าพรีวิวส่ง id มาครบไหม (API เก่าไม่ส่ง) ถ้าไม่ครบ
// ก็ถอยไปโหมดเดิมคือยกมาทั้งสาขาแบบบอกให้รู้ตัว แทนที่จะโชว์ช่องติ๊กที่ไม่มีผลจริง
const canSelectItems = (preview) =>
  Array.isArray(preview?.itemsToCreate) && preview.itemsToCreate.every(it => it.id);

// Single source of truth for the form shape — it used to be spelled out in
// four places, which is how new fields kept getting missed.
const EMPTY_FORM = {
  name: '',
  description: '',
  price: '',
  categoryId: '',
  imageUrl: '',
  isAvailable: true,
  adminOnly: false,
  unit: '',
  quantityPerServing: '1',
  barcode: ''
};


export default function MenuManagement({ apiUrl }) {
  const { isAllBranches, effectiveBranchId, branches, isOwner, currentBranch } = useBranch();
  const confirm = useConfirm();
  // เมนู/หมวดหมู่แยกต่อสาขาแล้ว — ต้องเลือกสาขาที่แน่นอนก่อนถึงจะโหลด/แก้ไขได้ เหมือนหน้า
  // จัดการหมวดหมู่และตั้งค่าเครื่องพิมพ์
  const blocked = isAllBranches || !effectiveBranchId;
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // Filter by category
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // NEW STATE: สำหรับเก็บสถานะว่ารูปภาพใดมีปัญหาในการโหลด
  const [imageError, setImageError] = useState({});

  // โคลนหมวดหมู่+เมนูจากสาขาอื่น — ไว้เริ่มสาขาใหม่ที่ยังไม่มีอะไรเลย
  const [showClone, setShowClone] = useState(false);
  const [cloneFromId, setCloneFromId] = useState('');
  const [clonePreview, setClonePreview] = useState(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  // id ของเมนูที่ติ๊กไว้ในพรีวิว — ค่าเริ่มต้นคือติ๊กครบทุกอัน (เท่ากับพฤติกรรมเดิมคือยกมาทั้งสาขา)
  const [cloneSelected, setCloneSelected] = useState(() => new Set());

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadMenu();
    loadCategories();
  }, [effectiveBranchId, blocked]);

  const loadMenu = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/menu`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const result = await response.json();
      const data = result.data || result; // Handle both {data: []} and [] formats
      // ถ้าโดนบล็อกสิทธิ์ (403) หรือพลาดแบบอื่น result เป็น { error: {...} } ไม่ใช่ array —
      // set ตรงๆ แล้ว items.reduce() ด้านล่างจะ throw ทำหน้าเว็บพังทั้งหน้า ต้อง fallback เป็น [] แทน
      if (!Array.isArray(data)) {
        console.error('❌ Expected array from /menu, got:', typeof data, data);
        setMenuItems([]);
        return;
      }
      setMenuItems(data);
    } catch (err) {
      console.error('Failed to load menu:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const data = result.data || result;
      if (!Array.isArray(data)) {
        console.error('❌ Expected array from /categories, got:', typeof data, data);
        setCategories([]);
        return;
      }
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;

    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(`${apiUrl}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const result = await response.json();
      return result.data.url;
    } catch (err) {
      console.error('Failed to upload image:', err);
      setMessage({ type: 'error', text: 'อัปโหลดรูปไม่สำเร็จ' });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Upload image first if selected
      let imageUrl = formData.imageUrl;
      if (selectedFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          return; // Stop if upload failed
        }
      }

      const token = localStorage.getItem('adminToken');
      const url = editingId ? `${apiUrl}/menu/${editingId}` : `${apiUrl}/menu`;
      const method = editingId ? 'PUT' : 'POST';

      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        categoryId: formData.categoryId,
        isAvailable: formData.isAvailable,
        adminOnly: formData.adminOnly,
        imageUrl: imageUrl || undefined,
        // Always sent: a blank value is how you clear a barcode
        barcode: formData.barcode
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setFormData(EMPTY_FORM);
        setSelectedFile(null);
        setImagePreview(null);
        setEditingId(null);
        setShowForm(false);
        setShowScanner(false);
        loadMenu();
      } else {
        const error = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: error?.error?.message || error?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
      }
    } catch (err) {
      console.error('Failed to save menu item:', err);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      ...EMPTY_FORM,
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      categoryId: item.categoryId || '',
      imageUrl: item.imageUrl || '',
      isAvailable: item.isAvailable,
      adminOnly: item.adminOnly === true,
      unit: item.unit || '',
      quantityPerServing: String(item.quantityPerServing ?? 1),
      barcode: item.barcode || ''
    });
    if (item.imageUrl) {
      const imageUrl = item.imageUrl.startsWith('http')
        ? item.imageUrl
        : `${apiUrl}${item.imageUrl.startsWith('/') ? '' : '/'}${item.imageUrl}`;
      setImagePreview(imageUrl);
    }
    setShowForm(true);
  };

  const handleToggleAvailability = async (item) => {
    const token = localStorage.getItem('adminToken');
    const newStatus = !item.isAvailable;

    try {
      const response = await fetch(`${apiUrl}/menu/${item.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        // Send only the field being changed. Spreading the whole item would
        // write back stale unit/quantityPerServing/barcode from whenever the
        // page was loaded, clobbering anything changed since.
        body: JSON.stringify({ isAvailable: newStatus })
      });

      if (response.ok) {
        loadMenu();
      } else {
        setMessage({ type: 'error', text: 'ไม่สามารถเปลี่ยนสถานะได้' });
      }
    } catch (err) {
      console.error('Failed to toggle availability:', err);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
    }
  };

  const handleDelete = async (id, name) => {
    if (!await confirm(`คุณต้องการลบเมนู "${name}" หรือไม่?`, { title: 'ลบเมนู', confirmText: 'ลบ', danger: true })) {
      return;
    }

    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${apiUrl}/menu/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        loadMenu();
      } else {
        setMessage({ type: 'error', text: 'ไม่สามารถลบเมนูได้' });
      }
    } catch (err) {
      console.error('Failed to delete menu item:', err);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
    }
  };

  const handleCancel = () => {
    setFormData(EMPTY_FORM);
    setSelectedFile(null);
    setImagePreview(null);
    setEditingId(null);
    setShowForm(false);
    setShowScanner(false);
  };

  // Export menu to JSON
  const handleExportMenu = () => {
    try {
      const exportData = menuItems.map(item => ({
        name: item.name,
        description: item.description || '',
        price: item.price,
        categoryId: item.categoryId || '',
        imageUrl: item.imageUrl || '',
        isAvailable: item.isAvailable !== false,
        // Stock settings, so an export/import round-trip does not lose them
        unit: item.unit || '',
        quantityPerServing: item.quantityPerServing ?? 1,
        ...(item.barcode ? { barcode: item.barcode } : {})
      }));

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `menu-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `ดาวน์โหลดเมนูสำเร็จ (${exportData.length} รายการ)` });
    } catch (err) {
      console.error('Export failed:', err);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการดาวน์โหลด' });
    }
  };

  // โคลนหมวดหมู่+เมนูจากสาขาอื่น — ดูตัวอย่างก่อนเสมอ (dryRun) ก่อนจะยืนยันเขียนจริง
  const runClone = async (dryRun) => {
    if (!cloneFromId) return;
    setCloneLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/menu/clone-from-branch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fromBranchId: cloneFromId,
          toBranchId: effectiveBranchId,
          dryRun,
          // พรีวิวต้องขอมาทั้งสาขาเสมอ ไม่งั้นไม่มีรายการให้ติ๊กเลือก ส่วนตอนสร้างจริงส่งเฉพาะที่ติ๊กไว้
          ...(dryRun || !canSelectItems(clonePreview) ? {} : { itemIds: [...cloneSelected] })
        })
      });
      const result = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: result.message || 'เกิดข้อผิดพลาด' });
        return;
      }
      if (dryRun) {
        setClonePreview(result.data);
        setCloneSelected(new Set(
          canSelectItems(result.data) ? result.data.itemsToCreate.map(it => it.id) : []
        ));
      } else {
        setMessage({
          type: 'success',
          text: `สร้างหมวดหมู่ ${result.data.categoriesCreated} รายการ, เมนู ${result.data.itemsCreated} รายการ` +
            (result.data.errors.length ? `\n⚠️ ${result.data.errors.length} รายการมีปัญหา (บาร์โค้ดชนกัน)` : '')
        });
        setShowClone(false);
        setClonePreview(null);
        setCloneFromId('');
        setCloneSelected(new Set());
        loadMenu();
        loadCategories();
      }
    } catch (err) {
      console.error('Clone failed:', err);
      setMessage({ type: 'error', text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้' });
    } finally {
      setCloneLoading(false);
    }
  };

  // Import menu from JSON
  const handleImportMenu = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      if (!Array.isArray(importData)) {
        setMessage({ type: 'error', text: 'รูปแบบไฟล์ไม่ถูกต้อง: ต้องเป็น Array' });
        return;
      }

      // Ask user: Replace all or Add to existing
      const clearExisting = await confirm(
        `พบเมนู ${importData.length} รายการ\nเมนูเก่าปัจจุบัน: ${menuItems.length} รายการ`,
        { title: 'นำเข้าเมนู', confirmText: 'แทนที่เมนูทั้งหมด', cancelText: 'เพิ่มเข้าไป (ไม่ลบเก่า)' }
      );

      if (clearExisting) {
        const confirmClear = await confirm(
          `คุณกำลังจะลบเมนูเก่าทั้งหมด ${menuItems.length} รายการ\nและนำเข้าเมนูใหม่ ${importData.length} รายการ\n\nยืนยันหรือไม่?`,
          { title: 'คำเตือน!', confirmText: 'ยืนยันแทนที่', danger: true }
        );

        if (!confirmClear) {
          e.target.value = '';
          return;
        }
      }

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/menu/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: importData,
          clearExisting: clearExisting
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'การนำเข้าล้มเหลว');
      }

      const result = await response.json();
      const data = result.data || result;

      let summaryText = '';
      if (clearExisting && data.deleted !== undefined) {
        summaryText += `ลบเมนูเก่า: ${data.deleted} รายการ\n`;
      }
      summaryText += `นำเข้าสำเร็จ: ${data.imported || 0} รายการ\n`;
      summaryText += `ข้าม (ชื่อซ้ำ): ${data.skipped || 0} รายการ\n`;
      summaryText += `ล้มเหลว: ${data.failed || 0} รายการ`;

      setMessage({ type: 'success', text: summaryText });

      loadMenu();
      e.target.value = ''; // Reset file input
    } catch (err) {
      console.error('Import failed:', err);
      setMessage({ type: 'error', text: `เกิดข้อผิดพลาด: ${err.message}` });
      e.target.value = ''; // Reset file input
    }
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — เมนู/หมวดหมู่แยกอิสระต่อสาขาแล้ว
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการตั้งค่าผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return <div>Loading menu...</div>;
  }

  // NEW FUNCTION: สำหรับจัดการเมื่อรูปภาพมีปัญหาในการโหลด
  const handleImageError = (itemId) => {
    setImageError(prev => ({ ...prev, [itemId]: true }));
  };
  
  // NEW FUNCTION: สำหรับจัดกลุ่มเมนูตาม Category
  const groupMenuItems = (items) => {
    const grouped = items.reduce((acc, item) => {
      const category = categories.find(cat => cat.id === item.categoryId);
      const categoryName = category ? category.name : 'Uncategorized';
      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }
      acc[categoryName].push(item);
      return acc;
    }, {});

    // เรียงลำดับตาม categories array (ถ้ามี)
    const orderedKeys = categories.map(cat => cat.name).filter(name => grouped[name]);
    if (grouped['Uncategorized']) {
      orderedKeys.push('Uncategorized');
    }
    
    return orderedKeys.map(key => ({
      category: key,
      items: grouped[key]
    }));
  };

  const groupedMenu = groupMenuItems(menuItems);

  return (
    <div>
      <Toast message={message} onDismiss={() => setMessage(null)} />
      <div className="page-header">
        <h2>🍽️ จัดการเมนูอาหาร</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowForm(true)} className="button">
            ➕ เพิ่มเมนูใหม่
          </button>
          <label className="button-secondary" style={{ cursor: 'pointer' }}>
            📥 นำเข้าเมนู (JSON)
            <input
              type="file"
              accept=".json"
              onChange={handleImportMenu}
              style={{ display: 'none' }}
            />
          </label>
          <button onClick={handleExportMenu} className="button-secondary">
            📤 ส่งออกเมนู (JSON)
          </button>
          {isOwner && branches.length > 1 && (
            <button
              onClick={() => { setShowClone(true); setClonePreview(null); setCloneFromId(''); setCloneSelected(new Set()); }}
              className="button-secondary"
            >
              🌿 โคลนจากสาขาอื่น
            </button>
          )}
        </div>
      </div>

      <Modal open={showClone} onClose={() => setShowClone(false)} title="🌿 โคลนหมวดหมู่+เมนูจากสาขาอื่น" maxWidth="600px">
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginTop: 0 }}>
          ดูตัวอย่างก่อน แล้ว<strong>ติ๊กเลือกเฉพาะเมนูที่อยากได้</strong> — สร้างให้เฉพาะรายการที่{' '}
          <strong>{currentBranch?.name}</strong> ยังไม่มี (จับคู่ด้วยชื่อ) ไม่แตะ ไม่ลบ ไม่ทับของที่มีอยู่แล้ว
          เรียกซ้ำได้ปลอดภัยเสมอ
        </p>
        <div className="form-group">
          <label>สาขาต้นทาง</label>
          <select className="dark-input" value={cloneFromId} onChange={(e) => { setCloneFromId(e.target.value); setClonePreview(null); setCloneSelected(new Set()); }}>
            <option value="">-- เลือกสาขาต้นทาง --</option>
            {branches.filter(b => b.id !== effectiveBranchId).map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        {!clonePreview && (
          <button
            className="button"
            disabled={!cloneFromId || cloneLoading}
            onClick={() => runClone(true)}
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            {cloneLoading ? '⏳ กำลังตรวจสอบ...' : '👀 ดูตัวอย่างก่อน'}
          </button>
        )}

        {clonePreview && (() => {
          const items = clonePreview.itemsToCreate;
          const selectable = canSelectItems(clonePreview);
          const selectedItems = selectable ? items.filter(it => cloneSelected.has(it.id)) : items;

          // หมวดหมู่ที่จะถูกสร้างจริงเดินตามเมนูที่ติ๊กไว้ — คิดฝั่งนี้ด้วยกติกาเดียวกับ backend
          // (clone-from-branch: สร้างเฉพาะหมวดหมู่ที่ยังไม่มีและมีเมนูที่เลือกไว้ต้องใช้)
          // เพื่อให้ตัวเลขบนปุ่มตรงกับที่เกิดขึ้นจริงทุกครั้งที่ติ๊กเพิ่ม/เอาออก
          const neededCats = new Set(selectedItems.map(it => it.category).filter(Boolean));
          const catsToCreate = clonePreview.categoriesToCreate.filter(c => neededCats.has(c));

          const setMany = (ids, on) => setCloneSelected(prev => {
            const next = new Set(prev);
            ids.forEach(id => (on ? next.add(id) : next.delete(id)));
            return next;
          });

          // จัดกลุ่มตามหมวดหมู่ของสาขาต้นทาง ให้ติ๊กยกหมวดได้ทีเดียวโดยไม่ต้องไล่ทีละรายการ
          const groups = [];
          const groupByName = new Map();
          for (const it of items) {
            const key = it.category || 'ไม่มีหมวดหมู่';
            if (!groupByName.has(key)) {
              const group = { name: key, items: [] };
              groupByName.set(key, group);
              groups.push(group);
            }
            groupByName.get(key).items.push(it);
          }

          return (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ padding: '0.75rem 1rem', background: 'var(--surface-2)', borderRadius: '8px', marginBottom: '0.75rem', color: 'var(--text)' }}>
                <div>🍽️ เมนูที่เลือกไว้: <strong>{selectedItems.length}</strong> จาก {items.length} รายการที่ยกมาได้</div>
                <div>📁 หมวดหมู่ที่จะสร้างตามไปด้วย: <strong>{catsToCreate.length}</strong>{catsToCreate.length > 0 && <span style={{ color: 'var(--muted)' }}> ({catsToCreate.join(', ')})</span>}</div>
                <div style={{ color: 'var(--muted)' }}>ข้ามไป (มีชื่อซ้ำอยู่แล้ว): {clonePreview.itemsSkipped.length}</div>
              </div>

              {items.length === 0 ? (
                <div style={{ color: 'var(--muted)', textAlign: 'center', padding: '1rem' }}>
                  ไม่มีเมนูใหม่ให้ยกมา — สาขานี้มีครบทุกรายการจากสาขาต้นทางแล้ว
                </div>
              ) : !selectable ? (
                <>
                  <div style={{ background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '8px', padding: '0.6rem 0.85rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    ⚠️ เซิร์ฟเวอร์ยังไม่รองรับการเลือกทีละเมนู (กำลังอัปเดตอยู่) — รอบนี้จะยกมาทั้งสาขา
                    ลองใหม่อีกครั้งในอีกสักครู่ถ้าอยากเลือกเฉพาะบางรายการ
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', color: 'var(--text-2)' }}>
                    {items.map((it, i) => (
                      <div key={i}>• {it.name} <span style={{ color: 'var(--muted)' }}>({it.category || 'ไม่มีหมวดหมู่'})</span></div>
                    ))}
                  </div>
                  <button className="button" disabled={cloneLoading} onClick={() => runClone(false)} style={{ width: '100%' }}>
                    {cloneLoading ? '⏳ กำลังสร้าง...' : `✅ ยืนยันสร้างทั้งหมด (${clonePreview.categoriesToCreate.length} หมวดหมู่ + ${items.length} เมนู)`}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <button type="button" className="button" style={smallButton} onClick={() => setMany(items.map(it => it.id), true)}>เลือกทั้งหมด</button>
                    <button type="button" className="button" style={smallButton} onClick={() => setCloneSelected(new Set())}>ไม่เลือกเลย</button>
                  </div>

                  <div style={{ maxHeight: '280px', overflowY: 'auto', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', color: 'var(--text-2)' }}>
                    {groups.map(group => {
                      const ids = group.items.map(it => it.id);
                      const checkedCount = ids.filter(id => cloneSelected.has(id)).length;
                      return (
                        <div key={group.name} style={{ marginBottom: '0.5rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={checkedCount === ids.length}
                              ref={el => { if (el) el.indeterminate = checkedCount > 0 && checkedCount < ids.length; }}
                              onChange={(e) => setMany(ids, e.target.checked)}
                            />
                            📁 {group.name} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({checkedCount}/{ids.length})</span>
                          </label>
                          {group.items.map(it => (
                            <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingLeft: '1.4rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={cloneSelected.has(it.id)}
                                onChange={(e) => setMany([it.id], e.target.checked)}
                              />
                              {it.name}
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    className="button"
                    disabled={cloneLoading || selectedItems.length === 0}
                    onClick={() => runClone(false)}
                    style={{ width: '100%' }}
                  >
                    {cloneLoading
                      ? '⏳ กำลังสร้าง...'
                      : selectedItems.length === 0
                        ? 'เลือกเมนูที่จะยกมาก่อน'
                        : `✅ ยืนยันสร้าง (${catsToCreate.length} หมวดหมู่ + ${selectedItems.length} เมนู)`}
                  </button>
                </>
              )}
            </div>
          );
        })()}
      </Modal>

      <Modal open={showForm} onClose={handleCancel} title={editingId ? '✏️ แก้ไขเมนู' : '➕ เพิ่มเมนูใหม่'} maxWidth="800px">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>ชื่อเมนู</label>
                  <input
                    type="text"
                    className="dark-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="เช่น หมูสไลซ์, เนื้อวากิว"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>รายละเอียด</label>
                  <textarea
                    className="dark-input"
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="รายละเอียดเมนู (ถ้ามี)"
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>ราคา (บาท)</label>
                  <input
                    type="number"
                    className="dark-input"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>หมวดหมู่</label>
                  <select
                    className="dark-input"
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    required
                  >
                    <option value="">-- เลือกหมวดหมู่ --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>รูปภาพ</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ padding: '0.5rem' }}
                  />
                  {imagePreview && (
                    <div style={{ marginTop: '1rem', maxWidth: '200px' }}>
                      <img src={imagePreview} alt="ตัวอย่างรูป" style={{ width: '100%', borderRadius: '12px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }} />
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="isAvailable"
                    checked={formData.isAvailable}
                    onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                    style={{ width: 'auto' }}
                  />
                  <label htmlFor="isAvailable" style={{ marginBottom: 0 }}>
                    ✅ พร้อมจำหน่าย
                  </label>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="adminOnly"
                    checked={formData.adminOnly}
                    onChange={(e) => setFormData({ ...formData, adminOnly: e.target.checked })}
                    style={{ width: 'auto', marginTop: '0.25rem' }}
                  />
                  <label htmlFor="adminOnly" style={{ marginBottom: 0 }}>
                    🔒 เฉพาะแอดมิน (ลูกค้าไม่เห็นในเมนู QR)
                    <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 400 }}>
                      เช่น ค่าเปิดเหล้า, ปรับอาหารเหลือ — พนักงานยังเพิ่มให้โต๊ะจากหน้าแก้ไขโต๊ะได้
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* บาร์โค้ดอยู่ที่นี่เพราะเป็นตัวระบุตัวตนของเมนู ส่วนหน่วยนับ
                ปริมาณต่อจาน และต้นทุน ย้ายไปหน้าจัดการสต็อกทั้งหมด เพื่อให้
                การตั้งค่าสต็อกอยู่ที่เดียว ไม่กระจายอยู่สามที่ */}
            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1.25rem',
              borderTop: '1px solid rgba(255,255,255,0.15)'
            }}>
              <div className="form-group" style={{ maxWidth: '380px' }}>
                <label>บาร์โค้ด</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    className="dark-input"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    placeholder="สแกนหรือพิมพ์"
                  />
                  <button
                    type="button"
                    className="button"
                    onClick={() => setShowScanner(!showScanner)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {showScanner ? 'ปิด' : 'สแกน'}
                  </button>
                </div>
                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>
                  เว้นว่างเพื่อลบบาร์โค้ด · หน่วยนับ ปริมาณต่อจาน และต้นทุน ตั้งที่หน้าจัดการสต็อก
                </small>
              </div>

              {showScanner && (
                <div style={{ marginTop: '1rem' }}>
                  <BarcodeScanner
                    isActive={showScanner}
                    onScan={(code) => {
                      setFormData(prev => ({ ...prev, barcode: code }));
                      setShowScanner(false);
                    }}
                    onError={(err) => console.error('[MenuManagement] scan error:', err)}
                  />
                </div>
              )}
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={handleCancel} className="button-secondary">
                ❌ ยกเลิก
              </button>
              <button type="submit" className="button" disabled={uploading}>
                {uploading ? '⏳ กำลังอัปโหลด...' : editingId ? '💾 บันทึก' : '➕ เพิ่ม'}
              </button>
            </div>
          </form>
      </Modal>

      {/* Category Filter Buttons */}
      <div style={{
        marginBottom: '2rem',
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        padding: '15px',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '8px'
      }}>
        <button
          onClick={() => setSelectedCategory(null)}
          style={{
            padding: '10px 20px',
            background: selectedCategory === null ? 'var(--accent)' : 'var(--surface-2)',
            color: selectedCategory === null ? '#1a1206' : 'var(--text-2)',
            border: 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease'
          }}
        >
          ทั้งหมด
        </button>
        {categories.map(cat => (
          <button
            key={cat._id || cat.id}
            onClick={() => setSelectedCategory(cat._id || cat.id)}
            style={{
              padding: '10px 20px',
              background: selectedCategory === (cat._id || cat.id) ? 'var(--accent)' : 'var(--surface-2)',
              color: selectedCategory === (cat._id || cat.id) ? '#1a1206' : 'var(--text-2)',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {groupedMenu.filter(group => {
        if (selectedCategory === null) return true;
        const categoryName = categories.find(c => (c._id || c.id) === selectedCategory)?.name;
        return group.category === categoryName;
      }).map((group) => (
        <div key={group.category} className="mb-5">
          <h3 className="category-header">{group.category}</h3>
          <div className="menu-list">
            {group.items.map((item, index) => (
              <div key={item.id || `menu-item-${index}`} className="menu-card">
                <div className="menu-card-image">
                  {/* NEW LOGIC: ใช้ state imageError ในการตัดสินใจว่าจะแสดงรูปภาพหรือ fallback */}
                  {item.imageUrl && !imageError[item.id] ? (
                    <img
                      src={item.imageUrl.startsWith('http') ? item.imageUrl : `${apiUrl}${item.imageUrl.startsWith('/') ? '' : '/'}${item.imageUrl}`}
                      alt={item.name}
                      // NEW: เรียกใช้ handleImageError ที่เราสร้างขึ้นใหม่
                      onError={() => handleImageError(item.id)}
                    />
                  ) : (
                    // Fallback content: แสดงเมื่อไม่มี URL หรือมีปัญหาในการโหลด
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3rem',
                      color: '#fff'
                    }}>
                      🍖
                    </div>
                  )}
                </div>
                <div className="menu-card-body">
                  <h5 className="menu-card-title">{item.name}</h5>
                  {item.adminOnly && (
                    <span style={{
                      alignSelf: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.15rem 0.6rem',
                      borderRadius: '999px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border-strong)',
                      color: 'var(--info)'
                    }}>
                      🔒 เฉพาะแอดมิน
                    </span>
                  )}
                  <p className="menu-card-description">{item.description}</p>
                  <div className="menu-card-footer">
                    <span className="menu-card-price">{item.price.toFixed(2)} ฿</span>
                    <div className="menu-card-actions">
                      <button onClick={() => handleEdit(item)} className="menu-card-btn edit">
                        ✏️ แก้ไข
                      </button>
                      <button onClick={() => handleDelete(item.id, item.name)} className="menu-card-btn delete">
                        🗑️ ลบ
                      </button>
                      <button
                        onClick={() => handleToggleAvailability(item)}
                        className="menu-card-btn"
                        style={{
                          background: item.isAvailable ? 'var(--warn)' : 'var(--ok)',
                          color: '#1a1206'
                        }}
                      >
                        {item.isAvailable ? '👁️ ซ่อน' : '✅ แสดง'}
                      </button>
                    </div>
                  </div>
                </div>
                {!item.isAvailable && (
                  <div className="availability-overlay" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '20px',
                    pointerEvents: 'none'
                  }}>
                    <span style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(255,255,255,0.9)',
                      borderRadius: '12px',
                      fontWeight: '600',
                      color: '#e74c3c'
                    }}>❌ ไม่พร้อมจำหน่าย</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

